import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { GameStateResponse, CombatReport, FleetSlot, HullClass } from '@galaxy/shared';
import { api } from '../api/client.ts';
import { initTelegramApp, triggerHaptic } from '../telegram/sdk.ts';

export type AuthStatus = 'authenticated' | 'authenticating' | 'expired' | 'unauthenticated' | 'error';

interface GameContextType {
  state: GameStateResponse | null;
  loading: boolean;
  error: string | null;
  notification: string | null;
  isDemo: boolean;
  token: string | null;
  authStatus: AuthStatus;
  authError: string | null;
  retryAuth: () => Promise<void>;
  combatModalReport: CombatReport | null;
  setCombatModalReport: (report: CombatReport | null) => void;
  clearNotification: () => void;
  refreshState: () => Promise<void>;
  upgradeBuilding: (buildingType: string) => Promise<void>;
  startResearch: (techId: string) => Promise<void>;
  saveBlueprint: (params: { name: string; hull_id: HullClass; modules: string[] }) => Promise<void>;
  produceShips: (designId: string, count: number) => Promise<void>;
  saveFleetFormation: (slots: FleetSlot[]) => Promise<void>;
  attackNpc: (npcId: string) => Promise<CombatReport>;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<GameStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [token, setToken] = useState<string | null>(() => api.getToken());
  const [authStatus, setAuthStatus] = useState<AuthStatus>('authenticating');
  const [authError, setAuthError] = useState<string | null>(null);
  const [combatModalReport, setCombatModalReport] = useState<CombatReport | null>(null);

  const showNotification = useCallback((msg: string) => {
    setNotification(msg);
    setTimeout(() => {
      setNotification((curr) => (curr === msg ? null : curr));
    }, 4000);
  }, []);

  // Initial authentication flow
  const initAuth = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAuthError(null);
    setAuthStatus('authenticating');

    const tgInfo = initTelegramApp();

    try {
      if (tgInfo.isInsideTelegram && tgInfo.initData) {
        // Inside Telegram: authenticate via cryptographically verified initData
        try {
          const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
          const startParam =
            (window.Telegram?.WebApp?.initDataUnsafe as any)?.start_param ||
            urlParams?.get('startapp') ||
            urlParams?.get('tgWebAppStartParam') ||
            undefined;

          const res = await api.loginTelegram(tgInfo.initData, startParam);
          setState(res.state);
          setToken(res.token);
          setIsDemo(false);
          setAuthStatus('authenticated');
        } catch (tgErr: any) {
          // If initData is expired, give explicit instructions
          const errMsg = tgErr.message || '';
          if (errMsg.includes('hết hạn') || errMsg.toLowerCase().includes('expired')) {
            setAuthStatus('expired');
            setAuthError('Phiên đăng nhập Telegram đã hết hạn! Vui lòng đóng và mở lại Mini App từ Telegram.');
            throw tgErr;
          }
          // If network error occurred, try falling back to existing valid token
          const existingToken = api.getToken();
          if (existingToken) {
            try {
              const currentState = await api.getState();
              setState(currentState);
              setToken(existingToken);
              setIsDemo(false);
              setAuthStatus('authenticated');
              return;
            } catch {
              api.clearToken();
              setToken(null);
            }
          }
          throw tgErr;
        }
      } else {
        // Outside Telegram (Web test / Development mode)
        const existingToken = api.getToken();
        if (existingToken) {
          try {
            const currentState = await api.getState();
            setState(currentState);
            setToken(existingToken);
            setIsDemo(existingToken.includes('demo'));
            setAuthStatus('authenticated');
            return;
          } catch {
            api.clearToken();
            setToken(null);
          }
        }
        // Fallback login demo
        const res = await api.loginDemo('Chỉ Huy Alpha');
        setState(res.state);
        setToken(res.token);
        setIsDemo(true);
        setAuthStatus('authenticated');
      }
    } catch (err: unknown) {
      console.error('Login error:', err);
      const errMsg = err instanceof Error ? err.message : 'Lỗi kết nối máy chủ';
      setError(errMsg);
      if (errMsg.includes('hết hạn') || errMsg.toLowerCase().includes('expired')) {
        setAuthStatus('expired');
        setAuthError(errMsg);
      } else {
        setAuthStatus('error');
        setAuthError(errMsg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Real-time local resource tick & queue timer check (every 1s)
  useEffect(() => {
    if (!state) return;

    const interval = setInterval(() => {
      setState((prev) => {
        if (!prev) return prev;

        const res = prev.planet.resources;
        const metalGainPerSec = res.metal_rate_hour / 3600;
        const he3GainPerSec = res.he3_rate_hour / 3600;
        const creditsGainPerSec = res.credits_rate_hour / 3600;

        const now = Date.now();

        // Check if any build queue item finished
        let queueFinished = false;
        if (prev.shipyard_queue.some((q) => q.finish_at <= now)) {
          queueFinished = true;
        }
        if (prev.buildings.some((b) => b.upgrade_finish_at && b.upgrade_finish_at <= now)) {
          queueFinished = true;
        }
        if (prev.research.some((r) => r.finish_at && r.finish_at <= now)) {
          queueFinished = true;
        }

        if (queueFinished) {
          // Trigger background sync
          api.getState().then((fresh) => setState(fresh)).catch(() => {});
        }

        return {
          ...prev,
          planet: {
            ...prev.planet,
            resources: {
              ...res,
              metal: Math.min(res.max_metal, res.metal + metalGainPerSec),
              he3: Math.min(res.max_he3, res.he3 + he3GainPerSec),
              credits: res.credits + creditsGainPerSec,
            },
          },
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state?.server_time]);

  const refreshState = useCallback(async () => {
    try {
      const fresh = await api.getState();
      setState(fresh);
    } catch (e: unknown) {
      console.error('Refresh state error:', e);
    }
  }, []);

  const upgradeBuilding = useCallback(async (buildingType: string) => {
    try {
      const res = await api.upgradeBuilding(buildingType);
      setState(res.state);
      triggerHaptic('success');
      showNotification('Đã bắt đầu nâng cấp công trình!');
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Nâng cấp thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  const startResearch = useCallback(async (techId: string) => {
    try {
      const res = await api.startResearch(techId);
      setState(res.state);
      triggerHaptic('success');
      showNotification('Đã bắt đầu nghiên cứu công nghệ!');
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Nghiên cứu thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  const saveBlueprint = useCallback(async (params: { name: string; hull_id: HullClass; modules: string[] }) => {
    try {
      const res = await api.saveBlueprint(params);
      setState(res.state);
      triggerHaptic('success');
      showNotification(`Bản vẽ [${params.name}] đã được lưu thành công!`);
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Lưu bản vẽ thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  const produceShips = useCallback(async (designId: string, count: number) => {
    try {
      const res = await api.produceShips(designId, count);
      setState(res.state);
      triggerHaptic('success');
      showNotification(`Đã đưa ${count} chiến hạm vào hàng chờ đóng tàu!`);
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Đóng tàu thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  const saveFleetFormation = useCallback(async (slots: FleetSlot[]) => {
    try {
      const res = await api.saveFleetFormation(slots);
      setState(res.state);
      triggerHaptic('success');
      showNotification('Đã lưu thành công bố trí đội hình 3×3!');
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Lưu đội hình thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  const attackNpc = useCallback(async (npcId: string): Promise<CombatReport> => {
    try {
      const res = await api.attackNpc(npcId);
      setState(res.state);
      setCombatModalReport(res.report);
      triggerHaptic(res.report.result === 'victory' ? 'success' : 'error');
      return res.report;
    } catch (err: unknown) {
      triggerHaptic('error');
      const msg = err instanceof Error ? err.message : 'Tấn công thất bại';
      showNotification(msg);
      throw err;
    }
  }, [showNotification]);

  return (
    <GameContext.Provider
      value={{
        state,
        loading,
        error,
        notification,
        isDemo,
        token,
        authStatus,
        authError,
        retryAuth: initAuth,
        combatModalReport,
        setCombatModalReport,
        clearNotification: () => setNotification(null),
        refreshState,
        upgradeBuilding,
        startResearch,
        saveBlueprint,
        produceShips,
        saveFleetFormation,
        attackNpc,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}
